/**
 * Custom hook for calculating and managing offer pricing based on employee count
 *
 * @param props - Hook parameters
 * @param props.offer - The pricing offer with ranges and configurations
 * @param props.nbEmployee - Number of employees for price calculation
 *
 * @returns {Object}
 * - pricesRange: Base prices with calculated total
 * - discount: Optional discounted prices if applicable
 *
 * The hook handles three main calculations:
 * 1. Base pricing:
 *    - Determines price range for employee count
 *    - Calculates base monthly total
 *
 * 2. Discount pricing:
 *    - Applies any available discounts
 *    - Calculates discounted monthly total
 *
 * 3. Price comparison:
 *    - Returns discount only if lower than base price
 *
 * @example
 * ```tsx
 * const PricingComponent = ({ offer }) => {
 *   const { pricesRange, discount } = useGetPrice({
 *     offer,
 *     nbEmployee: 10
 *   });
 *
 *   if (!pricesRange) {
 *     return <div>Contact sales for pricing</div>;
 *   }
 *
 *   return (
 *     <div>
 *       <p>Base price: ${pricesRange.base}/month</p>
 *       {discount && (
 *         <p>Discounted price: ${discount.total}/month</p>
 *       )}
 *     </div>
 *   );
 * };
 * ```
 */
import { useMemo } from 'react';
import getPrice from '../getPrice';
import { getPricesWithTotal } from '../getPricesWithTotal';
import {
  OfferProps,
  PricesRangeWithTotal,
  PricingRangeWithDiscountAndTotalProps,
} from '../../types';
import { calculateVariantPrice } from '../computePrice/calculateVariantPrice';
import { getDiscount } from '../getDiscount';

export type UseGetPriceProps = {
  offer: OfferProps;
  nbEmployee: number;
};
export type UseGetPriceReturn = {
  pricesRange: PricingRangeWithDiscountAndTotalProps | null;
  discount?: PricesRangeWithTotal | null;
};
export const useGetPrice = ({
  offer,
  nbEmployee,
}: UseGetPriceProps): UseGetPriceReturn => {
  const pricesRange = useMemo(() => {
    const pricesRangeFinded = getPrice(offer.pricingRange, nbEmployee);
    if (!pricesRangeFinded || pricesRangeFinded.onDemand) return null;
    return getPricesWithTotal(pricesRangeFinded, nbEmployee);
  }, [nbEmployee, offer.pricingRange]);

  const discountPrices = useMemo(() => {
    const pricesRangeFinded = getPrice(offer.pricingRange, nbEmployee);
    if (!pricesRangeFinded || pricesRangeFinded.onDemand) return null;
    return calculateVariantPrice({
      basePrice: pricesRangeFinded,
      variantPrice: pricesRangeFinded.discountPrices,
      nbEmployee,
    });
  }, [nbEmployee, offer.pricingRange]);

  const discount = useMemo(
    () => getDiscount({ pricesRange, discountPrices }),
    [pricesRange, discountPrices],
  );

  return { pricesRange, discount };
};
